import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheck, faHome, faReceipt, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Badge, Breadcrumb, Button, Card, Col, Form, InputGroup, Row, Spinner } from "@themesberg/react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { acknowledgeOrder, getOrderById, rejectOrder, updateOrderTotal } from "../api/orders.api";
import ConfirmDialog from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";

const formatDateTime = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const STATUS_VARIANTS = {
  pending: { bg: "secondary", text: "Pending" },
  completed: { bg: "warning", text: "Completed" },
  acknowledged: { bg: "success", text: "Acknowledged" },
  rejected: { bg: "danger", text: "Rejected" },
  cancelled: { bg: "dark", text: "Cancelled" }
};

const getStatusBadge = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  const config = STATUS_VARIANTS[normalizedStatus] || { bg: "light", text: status || "N/A" };

  return (
    <Badge bg={config.bg} className="px-3 py-2 text-uppercase small">
      {config.text}
    </Badge>
  );
};

const OrderDetailsSkeleton = () => (
  <>
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <div className="skeleton-text" style={{ width: "150px", height: "20px" }} />
        </div>
        <Row>
          <Col xs={12} md={4} className="mb-3">
            <div className="skeleton-text" style={{ width: "60px", height: "10px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "120px", height: "14px" }} />
          </Col>
          <Col xs={12} md={4} className="mb-3">
            <div className="skeleton-text" style={{ width: "40px", height: "10px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "160px", height: "14px" }} />
          </Col>
          <Col xs={12} md={4} className="mb-3">
            <div className="skeleton-text" style={{ width: "50px", height: "10px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "80px", height: "24px" }} />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <div className="skeleton-text" style={{ width: "70px", height: "10px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "140px", height: "14px" }} />
          </Col>
          <Col xs={12} md={6} className="mb-3">
            <div className="skeleton-text" style={{ width: "70px", height: "10px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "140px", height: "14px" }} />
          </Col>
        </Row>
      </Card.Body>
    </Card>

    {[...Array(3)].map((_, index) => (
      <Col xs={12} key={index} className="mb-3">
        <Card border="light" className="shadow-sm">
          <Card.Body>
            <div className="d-flex gap-3 align-items-center flex-wrap">
              <div className="skeleton-text" style={{ width: "84px", height: "84px", borderRadius: "1rem" }} />
              <div className="flex-grow-1">
                <div className="skeleton-text" style={{ width: "100px", height: "10px", marginBottom: "6px" }} />
                <div className="skeleton-text" style={{ width: "150px", height: "14px", marginBottom: "6px" }} />
                <div className="skeleton-text" style={{ width: "80px", height: "10px" }} />
              </div>
              <div>
                <div className="skeleton-text" style={{ width: "60px", height: "10px", marginBottom: "6px" }} />
                <div className="skeleton-text" style={{ width: "30px", height: "14px" }} />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    ))}

    <Card border="light" className="shadow-sm mt-4">
      <Card.Body>
        <div className="skeleton-text" style={{ width: "100px", height: "16px", marginBottom: "8px" }} />
        <div className="skeleton-text" style={{ width: "200px", height: "12px", marginBottom: "16px" }} />
        <div className="d-flex gap-3 align-items-center">
          <div className="skeleton-text" style={{ width: "200px", height: "38px" }} />
          <div className="skeleton-text" style={{ width: "100px", height: "38px" }} />
        </div>
      </Card.Body>
    </Card>
  </>
);

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const { showToast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [orderTotalValue, setOrderTotalValue] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    const loadOrder = async () => {
      setLoading(true);

      try {
        const response = await getOrderById(id);
        setOrder(response);
        setOrderTotalValue(String(response?.total_amount ?? ""));
      } catch (error) {
        showToast({ message: error.message || "Failed to load order details", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [id, showToast]);

  const normalizedStatus = String(order?.status || "").toLowerCase();
  const showPendingActions = normalizedStatus === "pending";

  const handleAcknowledge = async () => {
    setProcessingAction("acknowledge");

    try {
      const response = await acknowledgeOrder(id);
      setOrder(response.order);
      setOrderTotalValue(String(response.order?.total_amount ?? ""));
      showToast({ message: `Order #${id} acknowledged.`, variant: "success" });
    } catch (error) {
      showToast({ message: error.message || "Failed to acknowledge order", variant: "danger" });
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async (reason) => {
    setProcessingAction("reject");

    try {
      const response = await rejectOrder(id, reason);
      setOrder(response.order);
      setOrderTotalValue(String(response.order?.total_amount ?? ""));
      setShowRejectConfirm(false);
      showToast({ message: `Order #${id} rejected and stock restored.`, variant: "success" });
    } catch (error) {
      showToast({ message: error.message || "Failed to reject order", variant: "danger" });
    } finally {
      setProcessingAction(null);
    }
  };

  const handleOrderTotalSave = async () => {
    setProcessingAction("total");

    try {
      const response = await updateOrderTotal(id, orderTotalValue);
      setOrder(response.order);
      setOrderTotalValue(String(response.order?.total_amount ?? ""));
      showToast({ message: `Order #${id} total updated.`, variant: "success" });
    } catch (error) {
      showToast({ message: error.message || "Failed to update order total", variant: "danger" });
    } finally {
      setProcessingAction(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => history.push(Routes.Orders.path)}>Orders</Breadcrumb.Item>
            <Breadcrumb.Item active>Details</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Order Details</h4>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="outline-secondary" onClick={() => history.push(Routes.Orders.path)}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
        </div>
      </div>
      {loading ? (
        <OrderDetailsSkeleton />
      ) : !order ? null : (
        <>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
                <h5 className="mb-0">Order #{order.id}</h5>
              </div>
              <Row>
                <Col xs={12} md={4} className="mb-3">
                  <div className="small text-muted mb-1">Vendor</div>
                  <div className="fw-semibold">{order.vendor_name || "N/A"}</div>
                </Col>
                <Col xs={12} md={4} className="mb-3">
                  <div className="small text-muted mb-1">Email</div>
                  <div className="fw-semibold">{order.vendor_email || "N/A"}</div>
                </Col>
                <Col xs={12} md={4} className="mb-3">
                  <div className="small text-muted mb-1">Status</div>
                  <div>{getStatusBadge(order.status)}</div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="small text-muted mb-1">Created At</div>
                  <div className="fw-semibold">{formatDateTime(order.created_at)}</div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="small text-muted mb-1">Updated At</div>
                  <div className="fw-semibold">{formatDateTime(order.updated_at)}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            {Array.isArray(order.items) && order.items.length > 0 ? order.items.map((item) => (
              <Col xs={12} key={item.id} className="mb-3">
                <Card border="light" className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                      {item.image_url ? (
                        <img
                          src={resolveAssetUrl(item.image_url)}
                          alt={item.variant_name}
                          style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "1rem" }}
                        />
                      ) : (
                        <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "84px", height: "84px" }}>
                          N/A
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="small text-muted">{item.product_name}</div>
                        <h6 className="mb-1">{item.variant_name}</h6>
                        <div className="small text-muted">SKU: {item.sku}</div>
                      </div>
                      <div className="text-md-end">
                        <div className="small text-muted">Quantity</div>
                        <div className="fw-semibold">{item.quantity}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )) : (
              <Col xs={12}>
                <Card border="light" className="shadow-sm">
                  <Card.Body className="text-muted">No order items found.</Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Card border="light" className="shadow-sm mt-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                <div>
                  <h6 className="mb-1">Order Total</h6>
                  <div className="small text-muted">Set the final total and then accept or reject the order.</div>
                </div>
                <div>{getStatusBadge(order.status)}</div>
              </div>
              <Row className="align-items-end">
                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label>Set Total Price</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={orderTotalValue}
                        onChange={(event) => setOrderTotalValue(event.target.value)}
                        disabled={loading || Boolean(processingAction) || !showPendingActions}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={4} className="mt-4">
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      onClick={handleOrderTotalSave}
                      disabled={loading || Boolean(processingAction) || !showPendingActions}
                    >
                      {processingAction === "total" ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : null}
                      Save Total
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Row className="mt-4">
            <Col xs={12} className="d-flex gap-2  justify-content-end">
              {showPendingActions ? (
                <Button
                  variant="outline-success"
                  onClick={handleAcknowledge}
                  disabled={!order || Boolean(processingAction)}
                  style={{ cursor: !order || Boolean(processingAction) ? "not-allowed" : "pointer" }}
                >
                  {processingAction === "acknowledge" ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                  )}
                  Acknowledge
                </Button>
              ) : null}
              {showPendingActions ? (
                <Button
                  variant="outline-danger"
                  onClick={() => setShowRejectConfirm(true)}
                  disabled={!order || Boolean(processingAction)}
                  style={{ cursor: !order || Boolean(processingAction) ? "not-allowed" : "pointer" }}
                >
                  {processingAction === "reject" ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                  )}
                  Reject
                </Button>
              ) : null}
            </Col>
          </Row>
        </>
      )}
      <ConfirmDialog
        show={showRejectConfirm}
        title={order ? `Reject Order #${order.id}` : "Reject Order"}
        body={
          <>
            <p>
              Rejecting this order will restore stock to the affected variants. Do you want to continue?
            </p>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason (optional)"
            />
          </>
        }
        confirmLabel="Reject Order"
        confirmVariant="danger"
        processing={processingAction === "reject"}
        onCancel={() => {
          setShowRejectConfirm(false);
          setRejectReason("");
        }}
        onConfirm={() => {
          handleReject(rejectReason);
          setRejectReason("");
        }}
      />
    </>
  );
};
